import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  useGetLeaderboardQuery,
  useGetLeaderboardStandingQuery,
} from 'store/services/leaderboard/leaderboardApi';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';
import {
  groupForCategory,
  isPeriodless,
  CATEGORY_CONFIG,
  LEADERBOARD_GROUPS,
  LeaderboardCategory,
  LeaderboardGroup,
  LeaderboardPeriod,
  LeaderboardEntry,
} from 'types/interfaces/leaderboard';
import {
  LeaderboardGroupTabs,
  LeaderboardBoardTabs,
} from './components/LeaderboardTabs';
import LeaderboardFilters, {
  LeaderboardPeriodToggle,
} from './components/LeaderboardFilters';
import LeaderboardTable from './components/LeaderboardTable';
import YourStandingPanel from './components/YourStandingPanel/YourStandingPanel';
import AllTimeRecordsCard from './components/AllTimeRecordsCard/AllTimeRecordsCard';
import MobileRankBar, {
  MOBILE_RANK_BAR_HEIGHT,
} from './components/MobileRankBar/MobileRankBar';
import { parseTabParam } from './utils/leaderboardDisplay';
import './leaderboard.css';

const PERIODS: LeaderboardPeriod[] = ['week', 'month', 'all_time'];
const parsePeriod = (value: string | null): LeaderboardPeriod | null =>
  value && (PERIODS as string[]).includes(value)
    ? (value as LeaderboardPeriod)
    : null;

const LeaderboardPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isLoggedIn = !!currentUser;

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // URL-based state persistence for tab/filter navigation
  const [searchParams, setSearchParams] = useSearchParams();

  // ?tab= holds the BOARD (category), exactly as before, so every existing
  // link (?tab=most_points etc.) keeps working. The group is derived from it.
  // An unknown value falls back to the default board.
  const [category, setCategory] = useState<LeaderboardCategory>(() =>
    parseTabParam(
      searchParams.get('tab') ?? sessionStorage.getItem('leaderboard_tab')
    )
  );
  const [period, setPeriod] = useState<LeaderboardPeriod>(
    () =>
      parsePeriod(searchParams.get('period')) ??
      parsePeriod(sessionStorage.getItem('leaderboard_period')) ??
      'all_time'
  );
  const [country, setCountry] = useState<string>(
    () =>
      searchParams.get('country') ||
      sessionStorage.getItem('leaderboard_country') ||
      ''
  );
  const [search, setSearch] = useState<string>(
    () =>
      searchParams.get('search') ||
      sessionStorage.getItem('leaderboard_search') ||
      ''
  );
  const [page, setPage] = useState<number>(1);

  const group: LeaderboardGroup = groupForCategory(category);
  // Period-less boards hide the toggle and always request all_time; the
  // user's chosen period is kept for when they return to a periodic board.
  const effectivePeriod: LeaderboardPeriod = isPeriodless(category)
    ? 'all_time'
    : period;

  // Store all loaded rankings for pagination. The podium (ranks 1-3) only comes
  // with page 1, so it is kept separately: page 2+ responses carry an empty one.
  const [allRankings, setAllRankings] = useState<LeaderboardEntry[]>([]);
  const [podium, setPodium] = useState<LeaderboardEntry[]>([]);

  const { data, isLoading, isFetching } = useGetLeaderboardQuery({
    category,
    period: effectivePeriod,
    country: country || undefined,
    search: search || undefined,
    page,
    limit: 25,
    // Filter out users with empty want lists on Most Points (the standing
    // endpoint applies the same filter server-side).
    minWantListCount: category === 'most_points' ? 1 : undefined,
  });

  // The viewer on every board of the active group. Guests get 401, so skip.
  const { data: standing } = useGetLeaderboardStandingQuery(
    { group, period: effectivePeriod },
    { skip: !isLoggedIn }
  );

  // The podium (ranks 1-3) is the first rows of the one table, then every
  // loaded page of rankings.
  const rows = useMemo(
    () => [...podium, ...allRankings],
    [podium, allRankings]
  );

  // Subscribe to real-time balance updates for visible leaderboard users
  const leaderboardUserIds = useMemo(() => {
    const ids = rows.flatMap((e) => [
      e.userId ?? e.id,
      e.trade?.partner.userId,
    ]);
    return Array.from(
      new Set(ids.filter((id): id is number => typeof id === 'number'))
    );
  }, [rows]);

  useTraderBalanceSocket({
    traderIds: leaderboardUserIds,
    enabled: leaderboardUserIds.length > 0,
  });

  useEffect(() => {
    if (!isMobile) setIsMobileMenuOpen(false);
  }, [isMobile]);

  // Sync filter state to URL params and sessionStorage
  useEffect(() => {
    const params: Record<string, string> = {};
    if (category !== 'most_trades') params.tab = category;
    if (period !== 'all_time') params.period = period;
    if (country) params.country = country;
    if (search) params.search = search;
    setSearchParams(params, { replace: true });

    // Persist to sessionStorage so sidebar navigation restores state
    sessionStorage.setItem('leaderboard_tab', category);
    sessionStorage.setItem('leaderboard_period', period);
    sessionStorage.setItem('leaderboard_country', country);
    sessionStorage.setItem('leaderboard_search', search);
  }, [category, period, country, search, setSearchParams]);

  // Reset pagination and rankings when filters change
  useEffect(() => {
    setPage(1);
    setAllRankings([]);
    setPodium([]);
  }, [category, effectivePeriod, country, search]);

  // Update allRankings when new data arrives
  useEffect(() => {
    if (data) {
      if (data.page === 1) {
        setPodium(data.podium || []);
        setAllRankings(data.rankings);
      } else {
        setAllRankings((prev) => [...prev, ...data.rankings]);
      }
    }
  }, [data]);

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  }, [data?.hasMore, isFetching]);

  const handleSearchChange = useCallback(
    (newSearch: string) => setSearch(newSearch),
    []
  );
  const handleCountryChange = useCallback(
    (newCountry: string) => setCountry(newCountry),
    []
  );
  const handlePeriodChange = useCallback(
    (newPeriod: LeaderboardPeriod) => setPeriod(newPeriod),
    []
  );
  const handleSelectBoard = useCallback(
    (next: LeaderboardCategory) => setCategory(next),
    []
  );

  // A group click selects that group's first board; period, country and
  // search are kept.
  const handleSelectGroup = useCallback((next: LeaderboardGroup) => {
    const target = LEADERBOARD_GROUPS.find((g) => g.key === next);
    if (target) setCategory(target.boards[0]);
  }, []);

  // A record row opens its board on All Time.
  const handleSelectRecord = useCallback((next: LeaderboardCategory) => {
    setCategory(next);
    setPeriod('all_time');
  }, []);

  const ranks = useMemo(() => {
    const out: Partial<Record<LeaderboardCategory, number | null>> = {};
    standing?.boards.forEach((b) => {
      out[b.category] = b.rank;
    });
    return out;
  }, [standing]);

  const activeStandingBoard =
    standing && standing.group === group && standing.period === effectivePeriod
      ? (standing.boards.find((b) => b.category === category) ?? null)
      : null;
  const showMobileBar = isMobile && isLoggedIn && !!activeStandingBoard;

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Leaderboard - Top Traders | TradeMagic"
        description="Discover the most active Magic: The Gathering traders on TradeMagic. View rankings, ratings, and trade history for our top community members."
        canonical="https://trademagic.gg/leaderboard"
        keywords="MTG trading leaderboard, Magic card traders, TradeMagic rankings, top traders, Magic The Gathering trading"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Leaderboard', url: 'https://trademagic.gg/leaderboard' },
        ]}
      />

      <div className="leaderboard-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="leaderboard-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="leaderboard-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/leaderboard', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="leaderboard-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title="Leaderboard"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={() => setIsMobileMenuOpen((v) => !v)}
            />

            <div
              className="leaderboard-container"
              style={
                showMobileBar
                  ? { paddingBottom: MOBILE_RANK_BAR_HEIGHT + 16 }
                  : undefined
              }
            >
              {/* Page Header */}
              <header className="leaderboard-header">
                <h1 className="leaderboard-title">Leaderboard</h1>
                <p className="leaderboard-subtitle">
                  The most active traders in the TradeMagic community
                </p>
              </header>

              {/* Group tabs span the full width; both columns start below them. */}
              <LeaderboardGroupTabs
                activeCategory={category}
                onSelectGroup={handleSelectGroup}
                isMobile={isMobile}
              />

              <div className="leaderboard-columns">
                <div className="leaderboard-primary">
                  <div className="leaderboard-toolbar-row">
                    <LeaderboardBoardTabs
                      activeCategory={category}
                      onSelectBoard={handleSelectBoard}
                      isMobile={isMobile}
                      ranks={ranks}
                    />
                    {!isPeriodless(category) && (
                      <LeaderboardPeriodToggle
                        activePeriod={period}
                        onPeriodChange={handlePeriodChange}
                      />
                    )}
                  </div>

                  <div className="leaderboard-toolbar-row leaderboard-toolbar-row--secondary">
                    <p className="leaderboard-board-description">
                      {CATEGORY_CONFIG[category].description}
                    </p>
                    <LeaderboardFilters
                      country={country}
                      onCountryChange={handleCountryChange}
                      search={search}
                      onSearchChange={handleSearchChange}
                    />
                  </div>

                  <LeaderboardTable
                    rows={rows}
                    category={category}
                    isLoading={isLoading && page === 1}
                    currentUserId={currentUser?.id}
                    onLoadMore={handleLoadMore}
                    hasMore={data?.hasMore}
                    loadingMore={isFetching && page > 1}
                    isMobile={isMobile}
                  />

                  {/* Result count */}
                  {data && !isLoading && (
                    <div className="leaderboard-result-count">
                      Showing {rows.length} of {data.totalCount} traders
                      {country && ` from ${country}`}
                      {search && ` matching "${search}"`}
                    </div>
                  )}
                </div>

                {!isMobile && (
                  <aside className="leaderboard-aside">
                    <YourStandingPanel
                      group={group}
                      period={effectivePeriod}
                      isLoggedIn={isLoggedIn}
                      standing={standing}
                      activeCategory={category}
                      onSelectBoard={handleSelectBoard}
                    />
                    <AllTimeRecordsCard onSelectRecord={handleSelectRecord} />
                  </aside>
                )}
              </div>
            </div>
          </div>
        </div>

        {showMobileBar && <MobileRankBar board={activeStandingBoard} />}

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default LeaderboardPage;
